// chunkObsolete.js
//
// Reconnaître un chunk disparu, et dire si c'est parce qu'un déploiement a
// remplacé le build pendant que l'onglet était ouvert.

/**
 * Un chunk manquant se signale differemment selon le navigateur et le bundler.
 * On ratisse large : un faux positif coute un rechargement inutile, un faux
 * negatif coute une page blanche.
 */
export function isChunkLoadError(error) {
  if (!error) return false;

  if (error.name === 'ChunkLoadError') return true;

  const message = String(error.message || '');
  return (
    /Loading chunk [\w-]+ failed/i.test(message) ||        // webpack
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||    // mini-css-extract
    /Failed to fetch dynamically imported module/i.test(message) || // Vite/Chrome
    /error loading dynamically imported module/i.test(message) ||   // Firefox
    /Importing a module script failed/i.test(message) ||            // Safari
    // Vite charge la FEUILLE DE STYLE d'un chunk avant le chunk lui-meme, et
    // signale son echec avec un message a lui : « Unable to preload CSS for
    // /assets/X-hash.css ». Il manquait a cette liste — un utilisateur pris
    // par un deploiement voyait donc l'ecran d'erreur au lieu du rechargement
    // silencieux que tout le reste de ce fichier organise. Vu en production
    // les 13 et 17/08/2026 (assets/TutoProf-*.css, assets/VideoSection-*.css).
    /Unable to preload CSS/i.test(message)                          // Vite
  );
}

const ENTREE = /\/assets\/(index-[A-Za-z0-9_-]+\.js)/;

/**
 * Le script d'entrée que CET onglet exécute, par son nom de fichier (haché).
 * Vide en développement, où il n'y a pas de build : on ne conclut alors rien.
 */
function entreeEnCours() {
  const scripts = document.querySelectorAll('script[src]');
  for (const s of scripts) {
    const m = (s.getAttribute('src') || '').match(ENTREE);
    if (m) return m[1];
  }
  return '';
}

/**
 * `true` UNIQUEMENT quand on est certain que le serveur sert un autre build que
 * celui de l'onglet : le `index.html` courant ne cite plus notre script d'entrée.
 *
 * Le message de Safari (« Importing a module script failed ») ne nomme aucun
 * fichier, donc le serveur ne peut pas séparer « un déploiement a emporté le
 * chunk » de « le chunk est là et le client ne l'a pas obtenu ». Le navigateur,
 * lui, le peut. Un onglet périmé est le cas NORMAL après chaque déploiement
 * (l'ErrorBoundary recharge) : le compter comme incident suffisait à franchir le
 * seuil de cinq du mail d'alerte (sept en cinq minutes le 30/09/2026).
 *
 * Toute incertitude (pas d'entrée connue, réseau, délai de 3 s) rend `false` :
 * mieux vaut un incident de trop qu'une panne muette.
 */
export async function ongletPerime() {
  try {
    const entree = entreeEnCours();
    if (!entree) return false;

    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const minuterie = ctrl ? setTimeout(() => ctrl.abort(), 3000) : null;
    try {
      const r = await fetch('/index.html', {
        cache: 'no-store',
        credentials: 'omit',
        signal: ctrl ? ctrl.signal : undefined,
      });
      if (!r.ok) return false;
      const html = await r.text();
      // Une page qui ne cite AUCUN script d'entrée n'est pas notre index.html
      // (page d'erreur d'un proxy, 404.html) : on ne conclut rien.
      if (!ENTREE.test(html)) return false;
      return !html.includes(entree);
    } finally {
      if (minuterie) clearTimeout(minuterie);
    }
  } catch (e) {
    return false;
  }
}
